import {
  confirmResetPassword,
  confirmSignIn,
  confirmSignUp,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  resendSignUpCode,
  resetPassword,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  signUp as amplifySignUp,
  updatePassword,
} from "aws-amplify/auth";

const toLegacyToken = (token) => {
  if (!token) return null;

  return {
    ...token,
    jwtToken: token.toString(),
    payload: token.payload || {},
    getJwtToken: () => token.toString(),
  };
};

const toLegacySession = (session) => {
  const idToken = toLegacyToken(session.tokens?.idToken);
  const accessToken = toLegacyToken(session.tokens?.accessToken);

  return {
    ...session,
    idToken,
    accessToken,
    refreshToken: session.tokens?.refreshToken,
    clockDrift: session.tokens?.clockDrift,
    getIdToken: () => idToken,
    getAccessToken: () => accessToken,
    getRefreshToken: () => session.tokens?.refreshToken,
  };
};

const toLegacyChallengeName = (signInStep) => {
  const challengeNames = {
    CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED: "NEW_PASSWORD_REQUIRED",
    CONFIRM_SIGN_IN_WITH_SMS_CODE: "SMS_MFA",
    CONFIRM_SIGN_IN_WITH_TOTP_CODE: "SOFTWARE_TOKEN_MFA",
    CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE: "CUSTOM_CHALLENGE",
  };

  return challengeNames[signInStep] || signInStep;
};

const currentSession = async () => {
  const session = await fetchAuthSession();

  if (!session.tokens?.idToken || !session.tokens?.accessToken) {
    throw new Error("No current authenticated user");
  }

  return toLegacySession(session);
};

const currentAuthenticatedUser = async () => {
  const [user, attributes, signInUserSession] = await Promise.all([
    getCurrentUser(),
    fetchUserAttributes(),
    currentSession(),
  ]);

  return {
    ...user,
    id: user.userId,
    attributes,
    signInUserSession,
    username: user.username,
  };
};

const currentUserInfo = async () => {
  const [user, attributes] = await Promise.all([
    getCurrentUser(),
    fetchUserAttributes(),
  ]);

  return {
    id: user.userId,
    username: user.username,
    attributes,
  };
};

const signIn = async (username, password) => {
  const result = await amplifySignIn({ username, password });

  if (result.isSignedIn) {
    return currentAuthenticatedUser();
  }

  return {
    ...result,
    challengeName: toLegacyChallengeName(result.nextStep?.signInStep),
  };
};

const completeNewPassword = async (_user, newPassword, userAttributes) => {
  const input = { challengeResponse: newPassword };

  if (userAttributes) {
    input.options = { userAttributes };
  }

  const result = await confirmSignIn(input);

  if (result.isSignedIn) {
    return currentAuthenticatedUser();
  }

  return {
    ...result,
    challengeName: toLegacyChallengeName(result.nextStep?.signInStep),
  };
};

const changePassword = async (_user, oldPassword, newPassword) => {
  await updatePassword({ oldPassword, newPassword });
  return "SUCCESS";
};

const signUp = ({ username, password, attributes, options, ...rest }) =>
  amplifySignUp({
    username,
    password,
    options: {
      ...options,
      userAttributes: attributes,
    },
    ...rest,
  });

const forgotPassword = (username) => resetPassword({ username });

const forgotPasswordSubmit = (username, confirmationCode, newPassword) =>
  confirmResetPassword({ username, confirmationCode, newPassword });

const resendSignUp = (username) => resendSignUpCode({ username });

export const Auth = {
  changePassword,
  completeNewPassword,
  confirmSignUp: (username, confirmationCode) =>
    confirmSignUp({ username, confirmationCode }),
  currentAuthenticatedUser,
  currentSession,
  currentUserInfo,
  forgotPassword,
  forgotPasswordSubmit,
  resendSignUp,
  signIn,
  signOut: amplifySignOut,
  signUp,
};
